import _ from "lodash";
import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  cloneElement,
  useImperativeHandle,
} from "react";
import PropTypes from "prop-types";
import ReactDOMServer from "react-dom/server";

// Third party
import classNames from "classnames";
import { connect } from "react-redux";
import { Trans } from "react-i18next";
import { nanoid } from "nanoid";
import axios from "lib/axiosInstance";

// Hook
import { useI18n } from "Hooks";
import { useCallbackRef } from "Utils/CustomHooks";

// Utils
import {
  onEnterOrSpaceKeyDown,
  getUpdatedMaxSize,
  isSafari,
  extractContentFromHTML,
  getStyleStrippedText,
  generateRandomId,
  htmlToText,
  isTouchableV3,
} from "Utils";
import {
  getIsSendButtonDisabled,
  moveCaretToEndOfContentEditable,
  getPromptLabelWithAttachments,
  shouldRemoveBrTagFromEnd,
  cleanLabel,
  getPlaceholder,
  isOnlySpanTag,
  processClipboardFiles,
  getClipboardFiles,
  handleFileUploadAndAdd,
  replaceMathEquation,
  checkIsSTTLanguageEnabled,
  removeHtmlWithContent,
  createLatexImageFromText,
  isLikelyLatexText,
} from "./modules/Utils";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { wrapPlaceholderWithSpan } from "ClassRoom/components/AddAssignment/LessonPlanningAssistant/components/PromptCards/PromptCards";
import ACLStore from "lib/aclStore";
import {
  getAssessmentFieldValue,
  createSystemEventMetadataMessage,
  getUnprocessedLinkAttachments,
  getAITutorToolNamesAndChoices,
  getLearningToolIcon,
  checkIfIntendedModalityCreated,
} from "Utils/AIUtils";
import {
  AI_TUTOR_LEARNING_TOOLS,
  AI_TRANSLATION_LANGUAGES,
  CODE_EDITOR_PROGRAMMING_LANGUAGES,
} from "@toddle-edu/common-utils";
import {
  extractAttachmentIdsFromValue,
  getCustomType,
  getIsAIResponseLoading,
} from "../../modules/Utils";
import { getSettingValue } from "Utils/ConfigurationUtils";

// Components
import TheatreModeV2 from "UIComponents/TheatreModeV2";
import AnimateHeight from "UIComponents/AnimateHeight";
import DropzoneComponent from "UIComponents/DropzoneComponent";
import UIBaseContentEditable from "UIComponents/UIBaseContentEditable";
import AILanguageSelection from "AppComponents/ConfigureAIVoice/components/AILanguageSelection";
import { CreateDropDown } from "Attachment";
import ConfigureAIVoice from "AppComponents/ConfigureAIVoice";
import AudioRecorderSelector from "UIComponents/AudioRecorderSelector";
import LinkifyModal from "./components/LinkifyModal";
import UIModal from "UIComponents/UIModal";
import MathEquationEditor from "UIComponents/MathEquationEditor";
import CaptureImage from "Post/routes/CreatePost/components/Screens/CaptureImage";
import CompactAudioWaveformPlayer from "UIComponents/CompactAudioWaveformPlayer";
import LearningToolsDropdown from "./components/LearningToolsDropdown";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import CircleLoading from "UIComponents/CircleLoading";

// Create context
import { useAIChatContext } from "UIComponents/AIChat/context/AIChatContext";

//Feature flag
import {
  getIsAITutorEnhancementsV2Enabled,
  getIsReactPlayerVideoModalityEnabled,
} from "Utils/ACLStoreUtils";

// Modules
import {
  getSpeechToTextTranscript,
  openLinkInNewTab,
  hideIntercom,
  createAIMessage,
} from "modules/Services";
import {
  deleteAIConversationAttachment,
  updateAIMessage,
} from "./modules/Module";
import { updateAIData } from "ClassRoom/modules/ClassRoomModule";
import { uploadAttachments } from "Post/modules/PostModule";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import {
  createAIModalityAttachment,
  updateAITutorModality,
  updateAttachment,
  deleteAiMessages,
} from "../../modules/AIChatModules";
import { getAttachmentFromCache } from "UIComponents/AIChat/modules/AIChatGraphqlHelpers";
import { updateShowAIQuickTip } from "Assessments/modules/AssessmentModule";

// Constants
import {
  METADATA_PROMPT_FIELDS,
  ANIMATED_PLACEHOLDER_TEXTS,
  ALLOWED_FILE_TYPES,
  CREATE_DROPDOWN_OPTIONS,
  INITIAL_DIALOG_STATE,
  MODAL_ERRORS,
  MAX_NUMBER_OF_FILES,
  MAX_SIZE_10_MEGA_BYTES,
  DIALOGS_INFO,
  OPTIONS,
  DEFAULT_FOOTER_OPTIONS,
  LEARNING_TOOLS_PLACEHOLDER,
  LEARNING_TOOL_PROMPTS,
} from "./modules/Constants";
import {
  getAttachmentId,
  isModalityAttachmentType,
} from "UIComponents/AIChat/modules/Utils";
import { ATTACHMENT_OMIT_ATTRIBUTES } from "store/static";

// Design system
import {
  IconButton,
  Modal,
  Button,
  Tooltip,
  MediaCard,
  DSProvider,
} from "@toddle-edu/ds-web";
import { elevations, fontStyles } from "@toddle-edu/ds-theme";
import {
  CaretClosedFilled,
  CaretOpenedFilled,
  InformationOutlined,
  MicrophoneOutlined,
  SquareFilled,
  SendFilled,
  CloseOutlined,
  WarningTriangleFilled,
  PencilLineOutlined,
  TextAlignLeftOutlined,
  VoiceWaveOutlined,
} from "@toddle-edu/ds-icons";

// SCSS
import classes from "./Footer.scss";

let ANIMATED_PLACEHOLDER_TEXT_INDEX = 0;
const CONTENT_EDITABLE_ID = "AI_INPUT_FIELD";
const ADDITIONAL_INSTRUCTIONS_ID = "additional-instructions";

const tooltipAlign = {
  attachment: {
    offset: [18, 0],
  },
  fields: {
    offset: [13, 0],
  },
};

const styles = {
  uiContentEditable: {
    fontSize: "1.4rem",
    lineHeight: "2rem",
    padding: "12px 16px",
    minHeight: "44px",
    maxHeight: "150px",
    overflowY: "auto",
    textAlign: "left",
    ...fontStyles.medium,
  },
  overlayInnerStyle: {
    maxWidth: "172px",
  },
  aiTutorRecorderStyle: { background: "none" },
  iconButtonStyle: {
    padding: "8px",
  },
  audioRecorderVisualizer: {
    borderRadius: 0,
    border: "none",
    borderTop: "1px solid var(--border-secondary)",
  },
  waveformContainerStyle: {
    backgroundColor: "var(--surface-secondary-enabled)",
  },
  speakingOnlyLoaderProps: {
    strokeColor: "#ffffffff",
    size: "large",
    loaderStyles: {
      backgroundColor: "var(--interactive-semantic-info)",
      borderRadius: "6px",
    },
  },
  fullWidth: {
    width: "100%",
  },
};

const COPY_TEX_SCRIPT_ID = "copy-tex";
const loadCopyTexScript = () => {
  const existingScript = document.getElementById(COPY_TEX_SCRIPT_ID);

  if (!existingScript) {
    const script = document.createElement("script");
    script.src = "/copy-tex/copy-tex.min.js";
    script.id = COPY_TEX_SCRIPT_ID;
    script.defer = true;
    document.head.appendChild(script);
  }
};

/* Animation is not working smoothly in safari so not using it as of now */
const AnimateHeightWrapper = props => {
  const { showContextList, children } = props;

  if (!isSafari()) return <AnimateHeight {...props} />;
  else return showContextList ? children : null;
};

const iconButtonProps = {
  dsVersion: "2.0",
  size: "medium",
  variant: "neutral",
  type: "plain",
};

const Footer = props => {
  const {
    placeholder,
    initialValue,
    selectedPrompt,
    onChangeSelectedPrompt,
    contextOptions,
    onClickContextOption,
    footerOptions,
    onClickFooterOption,
    entityId,
    parentType,
    onSendMessage,
    onClickStop,
    audioVersion,
    setToastMsg,
    updateAIData,
    hideIntercom,
    showAudioTranscript,
    assessmentDetails,
    updateAttachment,
    uploadAttachments,
    onAttachmentsUpdate,
    mathEquationLatex,
    isAttachmentUploading,
    customConfigureAIVoiceComp,
    customCreateDropdownOptions,
    openLinkInNewTab,
    handleFileUploadAndAdd,
    getSpeechToTextTranscript,
    deleteAIConversationAttachment,
    createAIModalityAttachment,
    footerInnerContainerStyle,
    getAttachmentFromCache,
    updateAIMessage,
    ttsAudioRegistry,
    deleteAiMessages,
    createAIMessage,
    handleIsGeneratingAIResponse,
    showAIQuickTip,
    updateShowAIQuickTip,
  } = props;

  const {
    value,
    setValue,
    entityType,
    entitySubType,
    currentAIState,
    videoPlayerRef,
    localAttachments,
    setLocalAttachments,
    lastUserMessageRef,
    setShouldScrollToBottom,
    setShouldRenderEmptySpace,
    setIsScrollingToBottom,
    onMediaClick: contextOnMediaClick,
    selectedModalityId,
    conversation,
    messageAttachments,
    isPendingAttachmentUpdate,
    isAIGeneratingResponse,
    setIsAIGeneratingResponse,
    footerRef: parentFooterRef,
    updateCallsQueueRef,
    modalitiesViewerFunctionsRef,
    selectedLearningTool,
    setSelectedLearningTool,
    workbookCompRef,
    editMessageId,
    setEditMessageId,
  } = useAIChatContext();

  const { t } = useI18n();

  const isAITutor = entitySubType === "ai_tutor";
  const isStudentAITutor = entitySubType === "ai_tutor_chat";

  const MAX_SCREEN_WIDTH = 1200;
  const isTouchableDevice = isTouchableV3(MAX_SCREEN_WIDTH);

  // ===== states ========
  const [isRecording, setIsRecording] = useState(false);
  const [showQuickTip, setShowQuickTip] = useState(false);
  const [isContentEditableFocused, setIsContentEditableFocused] = useState(
    false
  );
  const [isContentEditableDisabled, setIsContentEditableDisabled] = useState(
    false
  );
  const [showContextList, setShowContextList] = useState(false);
  const [selectedAttachmentId, setSelectedAttachmentId] = useState(null);
  const [linkifyModalKey, setLinkifyModalKey] = useState(false);
  const [isCreateDropdownVisible, setIsCreateDropdownVisible] = useState(false);
  const [currentDialogInfo, setCurrentDialogInfo] = useState(
    INITIAL_DIALOG_STATE
  );
  const [isAudioTranscriptLoading, setIsAudioTranscriptLoading] = useState(
    false
  );
  const [mediaViewerData, setMediaViewerModalState] = useState(null);
  const [selectedItem, setSelectedItem] = useState({ action: "", params: {} }); // { action : "ADD_MATH_EQUATION", params: { latex } }
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioTranscripts, setAudioTranscripts] = useState([]);
  const [audioAttachments, setAudioAttachments] = useState({});
  const [removingAttachmentId, setRemovingAttachmentId] = useState(null);
  const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);
  const [isWaitingForUpdates, setIsWaitingForUpdates] = useState(false);

  // ===== refs =========
  const valueRef = useRef(value);
  const footerRef = useRef(null);
  const recordButtonRef = useRef(null);
  const audioContainerRef = useRef(null);
  const contextSelectionContainerRef = useRef(null);
  const attachmentScrollRef = useRef(null);
  const attachmentsContainerRef = useRef(null);
  const isPendingAttachmentUpdateRef = useRef(isPendingAttachmentUpdate);
  const localAttachmentsRef = useRef(localAttachments);
  const messageAttachmentsRef = useRef(messageAttachments);
  const [contentEditableRef, setContentEditableRef] = useCallbackRef();
  const [audioRecorderNodeRef, setAudioRecorderNodeRef] = useCallbackRef();
  const [savedSelectionRef, setSavedSelectionRef] = useCallbackRef();
  const hasInitializedAttachments = useRef(false);
  const [focusedPlaceholderId, setFocusedPlaceholderId] = useState(null);

  // ===== utils =======
  const hideQuickTip = () => {
    updateShowAIQuickTip(false);
    setShowQuickTip(false);
  };

  // ==== variables =====
  const messages = [..._.get(conversation, "messages.edges", [])].reverse();
  const isChatEmpty = _.isEmpty(messages);

  const allFields = _.get(assessmentDetails, "allFields", []);

  const studentCommunicationMode = getAssessmentFieldValue({
    fields: allFields,
    uid: "studentCommunicationMode",
  });

  const isSpeakingOnly = studentCommunicationMode === "speaking_only";
  const isWritingOnly = studentCommunicationMode === "writing_only";

  const parentId = _.get(conversation, "id");
  const conversationMetadata = _.get(conversation, "metadata");
  const conversationAttachments = _.get(conversation, "attachments", []);

  const isBlob = attachment => _.startsWith(attachment?.url, "blob:");

  const { action: currentDialogAction, params: currentDialogParams = {} } =
    currentDialogInfo || {};

  const placeholderText = useMemo(() => {
    // set placeholder based on selectedLearningTool or fallback to default
    if (!_.isEmpty(selectedLearningTool) && isChatEmpty) {
      const { key } = selectedLearningTool || {};
      return t(LEARNING_TOOLS_PLACEHOLDER[key]);
    } else if (isAITutor) {
      // animated placeholder is shown for AI tutor
      return "";
    } else {
      return !_.isEmpty(placeholder)
        ? placeholder
        : t("classRoom:start_typing_to_get_suggestions");
    }
  }, [selectedLearningTool, isAITutor, placeholder, t]);

  // ===== effects ======

  // copy-tex allows copying of latex (rendered through katex plugin in react-markdown)
  useEffect(() => loadCopyTexScript(), []);

  useEffect(() => {
    const userMessage = _.findLast(
      messages,
      msg => msg?.node?.authorType === "user"
    );

    const learningTool = userMessage?.node?.metadata?.learningTool;

    const isModalityCreated = checkIfIntendedModalityCreated({
      parsedMessages: messages,
      learningTool,
    });

    if (!isModalityCreated && learningTool) {
      const selectedTool = _.find(AI_TUTOR_LEARNING_TOOLS, {
        key: learningTool,
      });
      if (!_.isEmpty(selectedTool)) {
        setSelectedLearningTool({ ...selectedTool });
      }
    }
  }, []);

  useEffect(() => {
    const conversationAttachments = conversation?.attachments;

    if (
      hasInitializedAttachments.current ||
      _.isEmpty(conversationAttachments)
    ) {
      return;
    }

    if (_.isEmpty(selectedPrompt) && linkifyModalKey !== "add_a_link") {
      setLocalAttachments(conversationAttachments);
      contextOnMediaClick({ attachment: _.first(conversationAttachments) });
    }

    hasInitializedAttachments.current = true;
  }, [
    conversation,
    contextOnMediaClick,
    setLocalAttachments,
    selectedPrompt,
    linkifyModalKey,
  ]);

  // some components show intercom on unmount (like CaptureImage)
  // so when the component is closed so hide the intercom
  useEffect(() => {
    if (_.isEmpty(selectedItem?.action)) hideIntercom();
  }, [selectedItem]);

  // set starting text on mount
  useEffect(() => {
    if (!_.isEmpty(initialValue)) {
      setValue(initialValue);
    }
  }, []);

  // update valueRef when value changes
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  // update isPendingAttachmentUpdateRef when isPendingAttachmentUpdate changes
  useEffect(() => {
    isPendingAttachmentUpdateRef.current = isPendingAttachmentUpdate;
  }, [isPendingAttachmentUpdate]);

  // update localAttachmentsRef when localAttachments changes
  useEffect(() => {
    localAttachmentsRef.current = localAttachments;
  }, [localAttachments]);

  // update messageAttachmentsRef when localAttachments changes
  useEffect(() => {
    messageAttachmentsRef.current = messageAttachments;
  }, [messageAttachments]);

  // programmatically close create dropdown on "Upload from device"
  useEffect(() => {
    if (isAttachmentUploading) setIsCreateDropdownVisible(false);
  }, [isAttachmentUploading]);

  // show quick tip when the content editable div is focused for the first time
  useEffect(() => {
    let timeout = null;

    if (isContentEditableFocused && showAIQuickTip && isChatEmpty) {
      // show toast after .3s
      setTimeout(() => setShowQuickTip(true), 300);
      // hide toast after 3s
      timeout = setTimeout(hideQuickTip, 3000);
    }
    return () => (timeout ? clearTimeout(timeout) : null);
  }, [showAIQuickTip, isChatEmpty, isContentEditableFocused]);

  useEffect(() => {
    //when chat is focused, clear workbook selection
    if (isContentEditableFocused && modalitiesViewerFunctionsRef?.current) {
      modalitiesViewerFunctionsRef.current?.checkAndClearWorkbookSelection?.();
    }
  }, [isContentEditableFocused, modalitiesViewerFunctionsRef]);

  // disable content editable when an action is selected, this make sure that user is only able to edit
  // the placeholder text (##type here##) in the selectedPrompt
  useEffect(() => {
    if (isAITutor) {
      if (!_.isEmpty(selectedPrompt) && !_.isEmpty(value)) {
        setIsContentEditableDisabled(true);
      } else {
        setIsContentEditableDisabled(false);
      }
    }
  }, [isAITutor, selectedPrompt, value]);

  const isAttachmentEmpty = _.size(localAttachments) === 0;

  // scroll to the end when new attachments are added
  const localAttachmentsSize = _.size(localAttachments);
  useEffect(() => {
    const scrollRef = attachmentScrollRef?.current;
    if (scrollRef && !isAttachmentEmpty) {
      setTimeout(() => {
        scrollRef.scrollTo({
          left: scrollRef.scrollWidth,
          behavior: "smooth",
        });
      }, 100);
    }
  }, [localAttachmentsSize, isAttachmentEmpty]);

  // when a prompt is selected this will set that prompt to the content editable div and focus on it
  useEffect(() => {
    if (!_.isEmpty(selectedPrompt)) {
      const {
        label,
        shouldFocusContentEditable = true,
        hideAdditionalInstructions = false,
      } = selectedPrompt || {};

      if (contentEditableRef?.current && label) {
        if (isAITutor) {
          if (hideAdditionalInstructions) {
            setValue(label);

            // Focus and position caret at end of editable span after DOM updates
            setTimeout(() => {
              if (contentEditableRef?.current) {
                contentEditableRef.current.focus();

                const editableSpan = contentEditableRef.current.querySelector(
                  'span[contenteditable="true"]'
                );

                if (editableSpan && editableSpan.childNodes.length > 0) {
                  const selection = window.getSelection();
                  const range = document.createRange();
                  const lastNode =
                    editableSpan.childNodes[editableSpan.childNodes.length - 1];
                  if (lastNode.nodeType === Node.TEXT_NODE) {
                    range.setStart(lastNode, lastNode.length);
                  } else {
                    range.setStartAfter(lastNode);
                  }
                  range.collapse(true);
                  selection.removeAllRanges();
                  selection.addRange(range);
                }

                setIsContentEditableFocused(true);
              }
            }, 0);
          } else {
            const updatedLabel = getPromptLabelWithAttachments({
              t,
              promptLabel: label,
              attachments: localAttachments,
            });

            const uniqueId = _.uniqueId("placeholder-");
            setValue(
              `<span contenteditable="false" style="user-select: auto;">${updatedLabel} <span id="${ADDITIONAL_INSTRUCTIONS_ID}" placeholder="${t(
                "ai:ai_tutor_prompt_selected_placeholder"
              )}" class="${
                classes.additionalInstructionSpan
              }" role="textbox" tabindex="0" contenteditable="true" data-placeholder-id="${uniqueId}"></span>
                </span> `
            );

            setTimeout(() => {
              moveCaretToEndOfContentEditable(contentEditableRef.current);
              setIsContentEditableFocused(true);
            }, 0);
          }
        } else {
          setValue(
            `<span contenteditable=false style="user-select: none;">${label}; </span> `
          );
        }

        setTimeout(() => {
          setShowContextList(!isEveryRequiredFieldFilled);
          if (shouldFocusContentEditable && !hideAdditionalInstructions)
            contentEditableRef?.current.focus();
        });
      }
    }
  }, [selectedPrompt]);

  // when the value of the content editable div changes, this will update the placeholder
  useEffect(() => {
    const _contentEditableRef = contentEditableRef?.current;

    if (_contentEditableRef) {
      const _placeholder = !_.isEmpty(placeholderText)
        ? placeholderText
        : getPlaceholder({
            t,
            value,
            selectedAction: selectedPrompt,
            isAITutor,
            isInitialState: isChatEmpty,
          });

      // specifically for firefox (or browsers with same issue) - https://stackoverflow.com/questions/5705580/contenteditable-firefox-br-tag
      if (
        shouldRemoveBrTagFromEnd({ selectedPrompt, ref: _contentEditableRef })
      ) {
        _contentEditableRef.removeChild(_contentEditableRef.lastChild);
      }

      // for AITutor, placeholder is shown with animated text
      if (
        isAITutor &&
        _.isEmpty(value) &&
        isChatEmpty &&
        _.isEmpty(selectedLearningTool)
      ) {
        _contentEditableRef.removeAttribute("placeholder");
        return;
      }

      // update placeholder through setAttribute as contentEditable div does not support placeholder attribute
      if (_contentEditableRef.getAttribute("placeholder") !== _placeholder) {
        _contentEditableRef.setAttribute("placeholder", _placeholder);
      }
    }
  }, [selectedPrompt, value, isAITutor, isChatEmpty, selectedLearningTool]);

  // ====== variables =======
  const isContextEnabled = !_.isEmpty(contextOptions);

  const attachment = _.find(conversationAttachments, {
    id: selectedAttachmentId,
  });

  const shouldRenderContentEditable = useMemo(() => {
    if (isAudioTranscriptLoading) {
      return !_.isEmpty(value);
    }

    return !isSpeakingOnly || isContextEnabled;
  }, [value, isAudioTranscriptLoading]);

  // empty spaces, new lines are represented as "&nbsp;", "<br>", "\n" in contenteditable
  // so simple trim will not work, hence we need to convert the html to text and then trim
  const isQueryEmpty = _.isEmpty(
    _.trim(
      htmlToText(value, {
        preserveNewlines: false,
        wordwrap: null,
      })
    )
  );

  const isAudioTranscriptEmpty =
    _.isEmpty(audioTranscripts) &&
    _.size(localAttachments) === 1 &&
    _.get(localAttachments, "[0].type") === "AUDIO";

  // check if any audio attachment has a blob URL (still processing)
  const hasAudioWithBlobURL =
    getIsAITutorEnhancementsV2Enabled() &&
    _.some(
      localAttachments,
      attachment =>
        attachment?.type === "AUDIO" && _.startsWith(attachment?.url, "blob:")
    );

  const isWritingMessage = getIsAIResponseLoading(currentAIState);

  const isSendButtonDisabled =
    (((isSpeakingOnly && !isContextEnabled) || isQueryEmpty) &&
      (isAttachmentEmpty ||
        (isAudioTranscriptEmpty && !getIsAITutorEnhancementsV2Enabled()))) ||
    isWritingMessage ||
    isAIGeneratingResponse ||
    isAttachmentUploading ||
    isAudioTranscriptLoading ||
    getIsSendButtonDisabled({
      value,
      isAITutor,
      selectedAction: selectedPrompt,
      isAttachmentUploading,
      attachments: localAttachments,
      isSpeakingOnly,
    });

  const filledContextOptionsCount = useMemo(() => {
    const filledContextOptions = _.filter(contextOptions, contextOption => {
      const value = _.get(contextOption, "value", "");
      return !_.isEmpty(value);
    });

    return _.size(filledContextOptions);
  }, [contextOptions]);

  const prerequisitesWithEmptyValue = _.filter(
    contextOptions,
    contextOption => {
      const { value, isRequired = false } = contextOption || {};
      return isRequired && _.isEmpty(value);
    }
  );

  const isEveryRequiredFieldFilled = _.isEmpty(prerequisitesWithEmptyValue);

  // ===== utils =======

  const formatValueForUnsupportedBrowsers = value => {
    let formattedValue = _.trim(value);

    if (formattedValue === "<br>") formattedValue = "";

    if (_.endsWith(formattedValue, "</span>"))
      formattedValue = formattedValue + " ";

    return formattedValue;
  };

  // when user tries to add a new math equation, save the current caret position
  // so that we can insert the new equation at the same position
  const saveCaretPosition = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      setSavedSelectionRef(selection.getRangeAt(0));
    }
  };

  const updateInputField = value => {
    if (showQuickTip) hideQuickTip();

    const formattedValue = formatValueForUnsupportedBrowsers(value);

    if (_.isEmpty(formattedValue) && !_.isEmpty(selectedPrompt)) {
      onChangeSelectedPrompt({});
      if (selectedLearningTool?.key === "video_explorer") {
        setSelectedLearningTool(null);
      }
    }
    setValue(formattedValue);
  };

  // add styles to the math equation image
  const highlightImage = e => {
    const image = e.target;
    image.style.backgroundColor = `rgba(60, 132, 244, 0.25)`;
    image.style.border = `1px solid rgb(60, 132, 244)`;
    image.style.userSelect = "none";
    image.style.caretColor = "transparent";

    // create a range and select the image
    const range = document.createRange();
    range.selectNode(image);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
  };

  // remove styles from the math equation image
  const removeHighlight = () => {
    const mathEquations = document.querySelectorAll(".math-equation");

    _.forEach(mathEquations, element => {
      element.style.backgroundColor = "";
      element.style.border = "";
      element.style.userSelect = "";
      element.style.caretColor = "";
    });
  };

  const scrollLastUserMessageToTop = () => {
    setTimeout(() => {
      if (lastUserMessageRef.current) {
        lastUserMessageRef.current.scrollIntoView({
          block: "start",
          inline: "nearest",
          behavior: "smooth",
        });
      }
    }, 100);
  };

  const withTimeout = (promise, ms = 1000) =>
    Promise.race([
      promise,
      new Promise(resolve =>
        setTimeout(() => resolve({ isCurrentVersion: false }), ms)
      ),
    ]);

  const checkWorkbookUploadStatus = async () => {
    if (workbookCompRef?.current) {
      const response = await withTimeout(
        workbookCompRef.current.sendMessageV2({
          MESSAGE_TOKEN: "CHECK_CURRENT_VERSION",
        }),
        1000
      );
      const isCurrentVersion = _.get(response, "isCurrentVersion", false);
      if (isCurrentVersion) {
        setIsWaitingForUpdates(true);
        await workbookCompRef?.current?.sendMessageV2({
          MESSAGE_TOKEN: "CHECK_UPLOAD",
        });
        setIsWaitingForUpdates(false);
      }
    }
  };

  const onSendMessageLocal = async (args = {}) => {
    // Restore focus to the content editable after sending message
    setTimeout(() => {
      if (contentEditableRef?.current) {
        contentEditableRef.current.focus();
        setIsContentEditableFocused(true);
      }
    }, 1000);

    // If attachment updates are pending, wait for them to complete
    if (isPendingAttachmentUpdateRef.current) {
      setIsWaitingForUpdates(true);
      const checkUpdatesComplete = () => {
        const maxWaitTime = 5000;
        const startTime = Date.now();

        return new Promise(resolve => {
          const interval = setInterval(() => {
            const elapsedTime = Date.now() - startTime;

            const hasBlockingModality = _.some(
              localAttachmentsRef.current,
              att =>
                isModalityAttachmentType(att, true) && !getAttachmentId(att)
            );

            const canProceed =
              !isPendingAttachmentUpdateRef.current && !hasBlockingModality;

            // timeout should NOT unblock if modality still has NEW_ id
            if (canProceed) {
              clearInterval(interval);
              resolve();
            }

            if (elapsedTime >= maxWaitTime && !hasBlockingModality) {
              clearInterval(interval);
              resolve();
            }
          }, 100);
        });
      };

      await checkUpdatesComplete();
      setIsWaitingForUpdates(false);
    }

    const hasWhiteboardInCache = _.some(
      localAttachmentsRef.current,
      attachment =>
        _.get(attachment, "type") === "WORKBOOK" &&
        isModalityAttachmentType(attachment)
    );
    if (hasWhiteboardInCache) {
      await checkWorkbookUploadStatus();
    }

    if (updateCallsQueueRef?.current) {
      setIsWaitingForUpdates(true);
      const checkUpdatesQueue = () => {
        return new Promise(resolve => {
          const interval = setInterval(() => {
            if (
              _.isEmpty(updateCallsQueueRef?.current) ||
              _.isEmpty(_.compact([updateCallsQueueRef?.current]))
            ) {
              clearInterval(interval);
              resolve();
            }
          }, 100);
        });
      };

      await checkUpdatesQueue();
      setIsWaitingForUpdates(false);
    }

    const promptFields = _.map(contextOptions, field =>
      _.pick(field, METADATA_PROMPT_FIELDS)
    );

    const content = _.cloneDeep(value);

    const replaceAttachmentSpansWithMarkdownLinks = htmlContent => {
      const attachments = localAttachmentsRef.current;
      const linkAttachments = _.filter(
        attachments,
        att => (att?.type === "LINK" || att?.type === "VIDEO") && att?.url
      );

      if (_.isEmpty(linkAttachments)) return htmlContent;

      const tempElement = document.createElement("div");
      tempElement.innerHTML = htmlContent;

      const attachmentSpans = tempElement.querySelectorAll(
        "[data-attachment-id]"
      );

      _.forEach(attachmentSpans, span => {
        const attachmentId = span.dataset.attachmentId;
        const attachment = _.find(linkAttachments, { id: attachmentId });
        if (attachment) {
          const displayName = attachment.name || attachment.url;
          span.replaceWith(
            document.createTextNode(`[${displayName}](${attachment.url})`)
          );
        }
      });

      return tempElement.innerHTML;
    };

    const parsedQuery = _.trim(
      replaceAttachmentSpansWithMarkdownLinks(content)
    );

    const { toolNames, toolChoice } = getAITutorToolNamesAndChoices({
      selectedLearningTool,
    });

    const currentVideoTimeStamp = videoPlayerRef?.current?.getCurrentTimeStamp?.();
    const completionState = videoPlayerRef?.current?.getCompletionState?.();

    // Create video context metadata message if timestamp exists
    let userMessageInput;
    if (
      !_.isEmpty(currentVideoTimeStamp) &&
      entitySubType === "ai_tutor_chat" &&
      !_.isEmpty(_.trim(content))
    ) {
      const messagePrepend = createSystemEventMetadataMessage({
        eventType: "USER_MESSAGE",
        eventData: {
          video_context: {
            timestamp: currentVideoTimeStamp,
          },
          completion_state: completionState,
        },
      });

      userMessageInput = [
        {
          authorType: "user",
          content,
          metadata: { messagePrepend },
        },
      ];
    }

    // a fallback flow for modalities in case if any update is missed
    for (const attachment of localAttachmentsRef.current) {
      try {
        if (!isModalityAttachmentType(attachment)) continue;

        const attachmentFromCache = getAttachmentFromCache({
          id: getAttachmentId(attachment),
        });

        if (_.isEmpty(attachmentFromCache)) continue;

        const { type, modalityAttachment } = attachment || {};
        const { id, modalityAttachment: cacheModalityAttachment } =
          attachmentFromCache || {};

        switch (type) {
          case "CODE_EDITOR":
          case "TODDLE_DOC": {
            const { content: localContent } = modalityAttachment || {};
            const { content: cachedContent } = cacheModalityAttachment || {};

            if (localContent !== cachedContent) {
              setIsWaitingForUpdates(true);
              await updateAttachment({
                id,
                modalityFile: {
                  ...(_.omit(cacheModalityAttachment, "__typename") || {}),
                  content: localContent,
                },
              });
            }
            break;
          }

          case "WORKBOOK": {
            const localJson = _.get(modalityAttachment, "content", {});
            const cachedJsonUrl = _.get(
              attachmentFromCache,
              "metadata.workbookUrl",
              {}
            );

            if (cachedJsonUrl) {
              setIsWaitingForUpdates(true);
              const response = await axios.get(cachedJsonUrl);
              const cachedJson = JSON.stringify(_.get(response, "data", {}));

              if (
                !_.isEmpty(localJson) &&
                !_.isEmpty(cachedJson) &&
                !_.isEqual(localJson, cachedJson)
              ) {
                await updateAITutorModality({
                  id,
                  jsonData: localJson,
                });
              }
            }
            break;
          }

          default:
            break;
        }
      } catch (error) {
        console.error("Failed to sync attachment:", attachment, error);
      }
    }

    const assistantMetadata = {
      ...(currentVideoTimeStamp
        ? {
            videoContext: {
              timestamp: currentVideoTimeStamp,
            },
          }
        : {}),
    };

    setValue("");
    setLocalAttachments([]);
    setAudioTranscripts([]);
    setShowContextList(false);
    setIsScrollingToBottom(true);
    setShouldScrollToBottom(true);
    setIsAIGeneratingResponse(true);
    handleIsGeneratingAIResponse?.(true);
    setShouldRenderEmptySpace(true);
    scrollLastUserMessageToTop();
    setIsWaitingForUpdates(false);

    await onSendMessage({
      content: parsedQuery,
      setContent: setValue,
      promptFields,
      audioAttachments,
      audioTranscripts,
      attachments: localAttachmentsRef.current,
      toolNames,
      toolChoice,
      userMessageInput,
      learningTool: selectedLearningTool?.key,
      ...(args || {}),
      assistantMetadata,
    });
    setIsAIGeneratingResponse(false);
    handleIsGeneratingAIResponse?.(false);
  };

  const resetChatBarStates = () => {
    setEditMessageId(null);
    setValue("");
    setLocalAttachments([]);
  };

  const onClickEditMessage = async () => {
    if (!editMessageId) return;

    const originalAttachments = _.get(
      _.find(messages, ({ node }) => node?.id === editMessageId),
      "node.attachments",
      []
    );

    const removedAttachments = _.differenceBy(
      originalAttachments,
      localAttachments,
      "id"
    );

    const addedAttachments = _.differenceBy(
      localAttachments,
      originalAttachments,
      "id"
    );

    await updateAIMessage({
      variables: {
        input: {
          id: editMessageId,
          content: value,
          addedAttachments,
          removedAttachments: _.map(removedAttachments, "id"),
        },
      },
    });

    resetChatBarStates();
  };

  useImperativeHandle(
    parentFooterRef,
    () => ({ onSendMessage: onSendMessageLocal }),
    []
  );

  const onClickStopLocal = async () => {
    if (!isStudentAITutor) onClickStop();
    setIsAIGeneratingResponse(false);

    const { unprocessedLinks } = getUnprocessedLinkAttachments(messages);

    if (!_.isEmpty(unprocessedLinks)) {
      _.forEach(unprocessedLinks, ({ id, metadata }) => {
        updateAttachment({
          id,
          metadata: {
            ...(metadata || {}),
            isDeleted: true,
          },
        });
      });
    } else {
      const lastUserMessage = _.findLast(
        messages,
        msg => msg?.node?.authorType === "user"
      );

      // Delete the last assistant message
      const lastAssistantMessage = _.findLast(
        messages,
        msg =>
          msg?.node?.authorType === "assistant" &&
          msg?.node?.parentMessage?.id === lastUserMessage?.node?.id
      );

      const lastAssistantMessageId = _.get(lastAssistantMessage, "node.id");
      const tempMsg = _.startsWith(lastAssistantMessageId, "NEW_");

      if (lastAssistantMessageId) {
        if (!isStudentAITutor) {
          createAIMessage({
            variables: {
              input: {
                conversationId: parentId,
                authorType: "interruptedResponse",
                content: "",
                parentMessageId: lastUserMessage?.node?.id,
              },
            },
          });
        }

        if (!tempMsg) {
          await deleteAiMessages({
            messageId: lastAssistantMessageId,
            conversationId: parentId,
          });
        }

        if (isStudentAITutor) onClickStop(parentId, lastUserMessage?.node?.id);
      }
    }
  };

  // if any prerequisite if not filled then on click of send button open the context window
  const openContextWindow = () => {
    if (!isEveryRequiredFieldFilled) setShowContextList(true);
  };

  // handles double click event on content editable to edit math equation
  const onDoubleClickContentEditable = e => {
    const latex = _.get(e, "target.dataset.latex");
    const equationId = _.get(e, "target.dataset.id");

    if (latex) {
      highlightImage(e);
      saveCaretPosition();
      setSelectedAction({
        action: OPTIONS.ADD_MATH_EQUATION,
        params: { latex, equationId },
      });
    }
  };

  const onBlurInputField = () => {
    hideQuickTip();
    setIsContentEditableFocused(false);
  };

  const handleAttachmentsUpdate = ({ id, type, label, prevAttachmentId }) => {
    onAttachmentsUpdate(
      {
        id,
        type,
        label,
        valueRef,
        prevAttachmentId,
        selectedAttachmentId,
        attachments: localAttachments,
      },
      callbackParams => {
        const { updatedLabel } = callbackParams || {};

        if (updatedLabel) {
          setValue(updatedLabel);
        }
      }
    );
  };

  const saveFocusedPlaceholderId = event => {
    if (!event) return;

    const placeholderId = _.get(event, "target.dataset.placeholderId"); // for content-editable-placeholder

    const valueToSet = placeholderId || null;

    if (valueToSet !== focusedPlaceholderId) {
      setFocusedPlaceholderId(valueToSet);
    }
  };

  const onClickContentEditable = async event => {
    saveCaretPosition();
    saveFocusedPlaceholderId(event);

    if (event.target && event.target.classList.contains("math-equation")) {
      const isHighlighted =
        event.target.style.backgroundColor === `rgba(60, 132, 244, 0.25)`;

      if (isHighlighted) removeHighlight();
      else highlightImage(event);
    } else {
      removeHighlight();
    }

    const isCloseIconClick = event.target.id === "data-suffix-icon";

    const attachmentElement = event.target.closest("#attachment-text");

    const attachmentId = _.get(attachmentElement, "dataset.attachmentId");

    // If close icon is clicked then delete the attachment
    if (isCloseIconClick) {
      try {
        handleAttachmentsUpdate({ id: attachmentId, type: "DELETE" });

        setLocalAttachments(prevAttachments =>
          _.filter(
            prevAttachments,
            attachment => attachment?.id !== attachmentId
          )
        );

        await deleteAIConversationAttachment({
          input: { attachmentIds: [attachmentId] },
          aiConversationId: parentId,
        });
      } catch (e) {}
      return;
    }

    // If attachment element is clicked then show the linkify modal
    if (attachmentElement?.contains(event.target)) {
      setLinkifyModalKey(true);
      const selectedAttachmentId =
        attachmentId === "undefined" ? null : attachmentId;
      setSelectedAttachmentId(selectedAttachmentId);
    }
  };

  const onFocusContentEditable = event => {
    setIsContentEditableFocused(true);
    saveFocusedPlaceholderId(event);
  };

  const onKeyUpContentEditable = event => {
    saveCaretPosition();
    saveFocusedPlaceholderId(event);
  };

  // Handle paste events for attachments
  const handlePaste = async event => {
    const clipboardData = event.clipboardData;
    event.preventDefault(); // Prevent default paste behavior

    if (!clipboardData) return;

    const pastedHTML = event.clipboardData.getData("text/html");
    const pastedText = event.clipboardData.getData("text/plain");
    let isTextHandled = false;

    // check if class contains 'math-equation'
    const hasMathEquationClass = /class=["']([^"']*\s)?math-equation(\s[^"']*)?["']/.test(
      pastedHTML
    );

    if (hasMathEquationClass) {
      onPasteMathEquation(pastedHTML);
      isTextHandled = true;
    } else if (isLikelyLatexText(pastedText)) {
      const latexImage = await createLatexImageFromText(pastedText);

      if (latexImage) {
        restoreCaretPositionAndInsertImage(latexImage);
        isTextHandled = true;
      }
    }

    if (!isTextHandled) {
      document.execCommand("insertText", false, pastedText);
      const updatedValue = contentEditableRef.current.innerHTML;
      updateInputField(updatedValue);
    }

    const { clipboardFiles } = getClipboardFiles(clipboardData);

    if (_.isEmpty(clipboardFiles)) {
      return;
    }

    event.preventDefault();

    try {
      const attachmentsToAdd = [];
      const uploadPromises = [];

      const processedFiles = await processClipboardFiles(clipboardFiles);
      const validFiles = customFilterFilesHandler(processedFiles);

      // Create upload promises for all files
      validFiles.forEach(file => {
        uploadPromises.push(
          handleFileUploadAndAdd({
            file,
            parentId,
            attachmentsToAdd,
          })
        );
      });

      // Upload all files in parallel
      if (!_.isEmpty(uploadPromises)) {
        await Promise.all(uploadPromises);
      }

      if (attachmentsToAdd.length > 0) {
        addAttachment(attachmentsToAdd);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // restore the caret position and insert the new image at the saved caret position
  const restoreCaretPositionAndInsertImage = params => {
    const { height, width, latex, verticalAlign, src } = params;

    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(savedSelectionRef.current);

    const img = document.createElement("img");
    img.src = src;
    img.className = "math-equation";
    img.height = height;
    img.width = width;
    img.style.verticalAlign = `${verticalAlign}px`;
    img.style.display = "inline";
    img.alt = latex;
    img.setAttribute("data-latex", latex);
    img.setAttribute("data-id", generateRandomId());

    savedSelectionRef.current.insertNode(img);
    savedSelectionRef.current.collapse(false);

    const updatedValue = contentEditableRef.current.innerHTML;
    updateInputField(updatedValue);
  };

  const onContentUploaded = attachments => {
    const processedAttachments = _.map(attachments, attachment => {
      return { id: generateRandomId(), ...attachment };
    });

    if (_.isEmpty(processedAttachments)) return;

    setLocalAttachments(prevAttachments => [
      ...prevAttachments,
      ...processedAttachments,
    ]);
  };

  const createNewModalityAttachment = async params => {
    const { name = "Untitled", type = "TODDLE_DOC" } = params;

    // Check for existing attachments with same name and type
    const existingAttachments = _.filter(
      localAttachmentsRef.current,
      attachment => {
        return attachment.type === type && _.startsWith(attachment.name, name);
      }
    );

    let version = 1;
    let updatedName = name;

    // Get the highest version from metadata
    const versions = _.map(existingAttachments, attachment =>
      _.get(attachment, "metadata.version", 1)
    );

    version = (_.max(versions) || 0) + 1;
    let counter = 0;

    const combinedAttachments = _.uniqBy(
      [...messageAttachmentsRef.current, ...localAttachmentsRef.current],
      "id"
    );

    const existingNames = _.map(combinedAttachments, "name");

    while (_.includes(existingNames, updatedName)) {
      counter++;
      updatedName = `${name} ${counter}`;
    }

    const extraParams = {
      metadata: {
        version,
      },
    };

    if (type === "CODE_EDITOR") {
      extraParams.modalityFile = {
        modalityType: "CODE_EDITOR",
        metadata: { language: CODE_EDITOR_PROGRAMMING_LANGUAGES[0].key },
      };
    }
    if (type === "WORKBOOK") {
      extraParams.metadata = {
        ...extraParams.metadata,
        isWorkbookWithPageView: true,
        isNewBackgroundToolbarEnabled: true,
        isNewShapesToolbarEnabled: true,
        numOfPages: 1,
      };
    }

    try {
      // Show full screen loader
      setShowFullScreenLoader(true);

      // Create ToddleDoc attachment on server
      const createdAttachments = await createAIModalityAttachment({
        type,
        name: updatedName,
        parentId,
        parentType: "AI_CONVERSATION",
        ...extraParams,
      });

      // Extract the attachment from results
      const modalityAttachment = _.last(createdAttachments);

      if (modalityAttachment) {
        // Add to local state
        onContentUploaded([modalityAttachment]);

        // Show in right side modality
        if (contextOnMediaClick) {
          contextOnMediaClick({ attachment: modalityAttachment });
        }
      }

      // Hide full screen loader
      setShowFullScreenLoader(false);

      // Close the modal
      setSelectedAction({ action: null });
    } catch (error) {
      if (error) setShowFullScreenLoader(false);
    }
  };

  const onCreateModalityAttachment = useCallback(async ({ key }) => {
    switch (key) {
      case "toddle_doc": {
        createNewModalityAttachment({ type: "TODDLE_DOC" });
        break;
      }
      case "code_editor": {
        createNewModalityAttachment({ type: "CODE_EDITOR" });
        break;
      }
      case "workbook": {
        createNewModalityAttachment({ type: "WORKBOOK" });
        break;
      }
    }
  }, []);

  // set selected action with prev and new state
  const setSelectedAction = params => {
    setSelectedItem(prevState => ({
      ...prevState,
      ...params,
    }));
  };

  // add single or multiple attachments to attachmentItems state
  const addAttachment = async attachments => {
    if (_.isEmpty(attachments)) return;

    const newAttachments = _.castArray(attachments).map(attachment => ({
      ...attachment,
      id: _.isEmpty(attachment.id) ? generateRandomId() : attachment.id,
    }));

    setLocalAttachments(prevItems => [...prevItems, ...newAttachments]);
  };

  const handlePlaceholderAnimationEnd = e => {
    const animatedTextContainer = e.target;

    // update the text index to be shown
    ANIMATED_PLACEHOLDER_TEXT_INDEX =
      (ANIMATED_PLACEHOLDER_TEXT_INDEX + 1) %
      _.size(ANIMATED_PLACEHOLDER_TEXTS);

    // update placeholder text
    animatedTextContainer.innerText = t(
      ANIMATED_PLACEHOLDER_TEXTS[ANIMATED_PLACEHOLDER_TEXT_INDEX]
    );

    // Reset animation by forcing a reflow
    animatedTextContainer.offsetHeight; // trigger reflow
    animatedTextContainer.style.animation = "none"; // Disable the animation
    animatedTextContainer.offsetHeight; // trigger reflow again
    animatedTextContainer.style.animation = ""; // Re-enable the animation;
  };

  const onCloseLinkifyModal = () => {
    setLinkifyModalKey(false);
  };

  const getLinkedAttachment = params => {
    const { id, url, linkType, title, mimeType, type, fileType } = params;

    const result = {
      id,
      url,
      name: title,
      mimeType,
      type: linkType === "URL" ? type : fileType,
    };

    return result;
  };

  const onInsertAttachment = async params => {
    const linkedAttachment = getLinkedAttachment(params);

    addAttachment(linkedAttachment);

    // Update content editable value if selectedPrompt exists (same as AIContextBar)
    if (!_.isEmpty(selectedPrompt)) {
      const { id, name } = linkedAttachment;
      handleAttachmentsUpdate({
        id,
        label: name,
        prevAttachmentId: selectedAttachmentId,
      });
    }

    setLinkifyModalKey(false);
    setSelectedAttachmentId(null);
  };

  const handleTranscription = async ({
    attachment,
    shouldReplaceContent = false,
  }) => {
    let tutorLanguageCode;
    if (checkIsSTTLanguageEnabled()) {
      const tutorLanguageId = _.find(
        _.get(assessmentDetails, "allFields", []),
        { uid: "tutorLanguage" }
      )?.value;

      tutorLanguageCode = _.get(
        _.find(AI_TRANSLATION_LANGUAGES, ({ id }) => id == tutorLanguageId),
        "code"
      );
    }

    const transcriptionPayload = {
      entityId,
      entityType: entitySubType,
      language: tutorLanguageCode,
    };

    if (
      ACLStore.can("FeatureFlag:EnableSTTLanguageInput") &&
      conversationMetadata?.languageCode
    ) {
      transcriptionPayload.language = conversationMetadata.languageCode;
    }

    transcriptionPayload.blobAttachment = attachment;

    const transcript = (await getSpeechToTextTranscript(transcriptionPayload))
      ?.transcript;

    if (transcript) {
      if (showAudioTranscript) {
        if (focusedPlaceholderId) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(value, "text/html");

          // Try data-placeholder-id first
          const target = doc.querySelector(
            `[data-placeholder-id="${focusedPlaceholderId}"]`
          );

          if (target) {
            target.textContent = (target.textContent || "") + transcript;
            const updatedValue = doc.body.innerHTML;
            updateInputField(updatedValue);

            setTimeout(() => {
              const domTarget = contentEditableRef.current?.querySelector(
                `[data-placeholder-id="${focusedPlaceholderId}"]`
              );

              if (domTarget) {
                moveCaretToEndOfContentEditable(domTarget);
              }
            }, 0);
          } else {
            updateInputField(`${value} ${transcript}`);
          }
        } else {
          updateInputField(`${value} ${transcript}`);
        }
      } else {
        setAudioTranscripts(prevState => [
          ...prevState,
          {
            id: isBlob ? `BLOB_AUDIO_${nanoid(5)}` : attachment?.id,
            transcript,
          },
        ]);

        if (!isSpeakingOnly) {
          updateInputField(prevValue => {
            if (!_.isEmpty(_.trim(prevValue)) && !shouldReplaceContent) {
              return `${prevValue} ${transcript}`;
            }

            return transcript;
          });
        }

        if (!_.isEmpty(selectedPrompt)) {
          updateInputField(`${transcript}`);
        } else {
          updateInputField(`${value} ${transcript}`);
        }
      }
    }

    if (!_.isEmpty(attachment?.id) && !showAudioTranscript)
      setAudioAttachments(prevState => {
        return {
          ...prevState,
          [attachment?.id]: attachment,
        };
      });

    setIsAudioTranscriptLoading(false);
    setIsContentEditableDisabled(false);
    onChangeSelectedPrompt({});
    if (selectedLearningTool?.key === "video_explorer") {
      setSelectedLearningTool(null);
    }

    if (contentEditableRef.current) {
      contentEditableRef.current.focus();
      moveCaretToEndOfContentEditable(contentEditableRef.current);
      setIsContentEditableFocused(true);
    }
  };

  const onBlobAttachmentRecieved = async blobAttachment => {
    if (getIsAITutorEnhancementsV2Enabled() && !showAudioTranscript) {
      setAudioBlob(blobAttachment);
      return;
    }

    setIsAudioTranscriptLoading(true);
    setIsContentEditableDisabled(true);

    if (!_.isEmpty(selectedPrompt)) {
      setValue("");
    }

    await handleTranscription({ attachment: blobAttachment });
  };

  const handleGenerateTranscript = async attachment => {
    setIsAudioTranscriptLoading(true);

    // Use stored audio blob if available, otherwise use the attachment as-is
    const blobAttachment = audioBlob || attachment;
    await handleTranscription({
      attachment: blobAttachment,
      shouldReplaceContent: true,
    });
  };

  // ===== handlers ======
  const handleRecording = useCallback(value => {
    const newIsRecording = !value;
    setIsRecording(newIsRecording);
  }, []);

  const handleLearningToolSelection = useCallback(
    tool => {
      setSelectedLearningTool(() => {
        // Set the selected prompt based on the tool
        if (tool?.key && LEARNING_TOOL_PROMPTS[tool.key]) {
          const shouldShowStarterTemplate =
            _.isEmpty(_.trim(value)) || !_.isEmpty(selectedPrompt);

          if (shouldShowStarterTemplate) {
            const toolPrompt =
              tool?.key === "video_explorer" &&
              getIsReactPlayerVideoModalityEnabled()
                ? t(LEARNING_TOOL_PROMPTS[tool.key]).replace(
                    "[YouTube URL]",
                    "[video]"
                  )
                : t(LEARNING_TOOL_PROMPTS[tool.key]);

            const label = wrapPlaceholderWithSpan({
              t,
              label: toolPrompt,
              showEditIcon: false,
            });

            onChangeSelectedPrompt({
              label,
              shouldFocusContentEditable: false,
            });
          } else if (
            tool.key === "video_explorer" &&
            !_.isEmpty(_.trim(value))
          ) {
            // When video_explorer is selected and user has existing text
            // Add YouTube video placeholder on new line
            const videoPlaceholder = wrapPlaceholderWithSpan({
              t,
              label: getIsReactPlayerVideoModalityEnabled()
                ? "##[video]##"
                : "##[URL]##",
            });

            // Wrap the entire YouTube video line in a non-editable span
            const videoLine = `<span contenteditable="false" style="user-select: auto;">Attachment: ${videoPlaceholder}</span>`;

            // Wrap user's existing text in an editable span without additional instructions
            const editableUserText = `<span contenteditable="true" id="user-editable" placeholder="${t(
              "ai:ai_tutor_chat_placeholder"
            )}" class="${classes.editableUserText}">${value}</span>`;

            // Create the full label with editable user text and video line
            const combinedLabel = `${editableUserText}<br>${videoLine}`;

            onChangeSelectedPrompt({
              label: combinedLabel,
              shouldFocusContentEditable: true,
              hideAdditionalInstructions: true,
            });
          }
        } else {
          onChangeSelectedPrompt({});
        }

        return { ...(tool || {}), shouldPassLearningToolToAI: true };
      });

      // Restore focus to content editable after learning tool selection
      const isVideoExplorerWithText =
        tool?.key === "video_explorer" && !_.isEmpty(_.trim(value));

      if (!isVideoExplorerWithText) {
        setTimeout(() => {
          if (contentEditableRef?.current) {
            contentEditableRef.current.focus();
            moveCaretToEndOfContentEditable(contentEditableRef.current);
            setIsContentEditableFocused(true);
          }
        }, 0);
      }
    },
    [onChangeSelectedPrompt, t, value, selectedPrompt, contentEditableRef]
  );

  const onKeyDown = e => {
    if (showQuickTip) hideQuickTip();

    if (
      e?.key === "Backspace" &&
      e?.target?.textContent === "" &&
      e?.target?.id === ADDITIONAL_INSTRUCTIONS_ID
    ) {
      setValue("");
      if (!_.isEmpty(selectedPrompt)) {
        onChangeSelectedPrompt({});
        if (selectedLearningTool?.key === "video_explorer") {
          setSelectedLearningTool(null);
        }
      }
      setTimeout(() => {
        if (contentEditableRef.current) {
          contentEditableRef.current.focus();
          moveCaretToEndOfContentEditable(contentEditableRef.current);
        }
      }, 0);
      return;
    }

    const isEnterPressed = e.keyCode === 13;
    const isShiftPressed = e.shiftKey;

    if (isEnterPressed && !isShiftPressed) {
      e.preventDefault();
      if (
        isEveryRequiredFieldFilled &&
        !isSendButtonDisabled &&
        !isAIGeneratingResponse
      ) {
        onSendMessageLocal();
      } else {
        openContextWindow();
      }
    }
  };

  const customFilterFilesHandler = files => {
    const numberOfFiles = _.size(files);
    const numberOfAttachments = _.size(conversationAttachments);

    // CASE 1 - show error model when number of files are more than 5
    if (numberOfFiles + numberOfAttachments > 5) {
      setCurrentDialogInfo({
        action: MODAL_ERRORS.FILE_COUNT,
        params: { label: MAX_NUMBER_OF_FILES },
      });

      setIsCreateDropdownVisible(false);

      return [];
    }

    // CASE 2 - show error model when file size is more than 10MB
    const filesWithinLimit = _.filter(files, file => {
      const { type, size, sizeBytes } = file || {};

      const updatedMaxSize = getUpdatedMaxSize({
        maxSize: MAX_SIZE_10_MEGA_BYTES,
        fileType: type,
      });

      return updatedMaxSize >= (size || sizeBytes || 0);
    });

    if (_.size(filesWithinLimit) !== _.size(files)) {
      const filesExceedingLimit = _.differenceBy(files, filesWithinLimit, [
        "preview",
        "url",
      ]);

      const names = _.map(filesExceedingLimit, "name");

      setCurrentDialogInfo({
        action: MODAL_ERRORS.FILE_SIZE,
        params: { label: names },
      });

      setIsCreateDropdownVisible(false);

      return filesWithinLimit;
    }

    // return files as it is if no error
    return files;
  };

  const onDropDownOptionClick = ({ key }) => {
    if (key === "add_a_link") {
      setLinkifyModalKey(key);
      setIsCreateDropdownVisible(false);
    }
  };

  const onFileUpload = ({ attachment }) => {
    if (!getIsAITutorEnhancementsV2Enabled() || showAudioTranscript) {
      setIsAudioTranscriptLoading(true);
      setIsContentEditableDisabled(true);
    }

    if (!showAudioTranscript) addAttachment(attachment);
  };

  // handle math equation paste
  const onPasteMathEquation = pastedHTML => {
    // extract required attributes
    const attributes = {};
    const heightMatch = pastedHTML.match(/height=["']([^"']+)["']/);
    const widthMatch = pastedHTML.match(/width=["']([^"']+)["']/);
    const latexMatch = pastedHTML.match(/data-latex=["']([^"']+)["']/);
    const srcMatch = pastedHTML.match(/src=["']([^"']+)["']/);
    const verticalAlignMatch = pastedHTML.match(/vertical-align:\s?(-?\d+px)/);

    // assign matches to attributes object if they exist
    if (heightMatch) attributes.height = Number(heightMatch[1]);
    if (widthMatch) attributes.width = Number(widthMatch[1]);
    if (latexMatch) attributes.latex = latexMatch[1];
    if (srcMatch) attributes.src = srcMatch[1];
    if (verticalAlignMatch)
      attributes.verticalAlign = parseInt(verticalAlignMatch[1], 10);

    restoreCaretPositionAndInsertImage(attributes);
  };

  const onClickExpandKnowledge = event => {
    event?.stopPropagation();

    if (!showContextList) contentEditableRef.current?.focus();
    setShowContextList(prevState => !prevState);
  };

  // delete attachment from attachmentItems state
  const deleteAttachment = ({ id }) => {
    const updatedAttachments = _.filter(
      localAttachments,
      attachment => attachment?.id !== id
    );

    const attachmentToDelete = _.find(
      localAttachments,
      attachment => attachment?.id === id
    );

    const isModalityAttachment = isModalityAttachmentType(attachmentToDelete);

    const showWarningDialog = !_.isEmpty(id) && isModalityAttachment;

    // if warning dialog is visible on delete then do not animate
    if (!showWarningDialog) {
      setRemovingAttachmentId(id);

      if (!_.startsWith(id, "NEW_")) {
        deleteAIConversationAttachment({
          input: { attachmentIds: [id] },
          aiConversationId: parentId,
        });
      }
    }

    setTimeout(() => {
      setLocalAttachments(updatedAttachments, attachmentToDelete);

      const isAudioAttachment = attachmentToDelete?.type === "AUDIO";

      if (isAudioAttachment) {
        setAudioTranscripts([]);
      }

      setRemovingAttachmentId(null);
    }, 300); // match the duration of the CSS transition
  };

  const deleteExistingAudioAttachment = () => {
    const existingAudio = _.find(
      localAttachments,
      att => att?.type === "AUDIO"
    );

    if (existingAudio?.id) {
      deleteAttachment({ id: existingAudio.id });
    }
  };

  // ===== components ======
  const renderKnowledgeBar = () => {
    const knowledgeBarContainerClass = classNames(
      classes.knowledgeBarContainer,
      {
        [classes.knowledgeBarForSpeakingOnly]:
          isSpeakingOnly && !isContextEnabled,
      }
    );

    const showFilledContextOptionsCount = () => {
      return (
        <div className={classNames(classes.knowledgeSources, "text-body-s")}>
          {t("common:count_selected", {
            count: filledContextOptionsCount,
          })}
          <IconButton
            dsVersion="2.0"
            type="fill"
            size="small"
            variant="neutral"
            icon={
              showContextList ? <CaretOpenedFilled /> : <CaretClosedFilled />
            }
          />
        </div>
      );
    };

    return (
      <div
        className={knowledgeBarContainerClass}
        tabIndex={0}
        role="button"
        onClick={onClickExpandKnowledge}
        onKeyDown={event =>
          onEnterOrSpaceKeyDown(event, onClickExpandKnowledge)
        }
      >
        {filledContextOptionsCount ? showFilledContextOptionsCount() : null}
      </div>
    );
  };

  const renderPlaceholderWithAnimation = () => {
    const showPlaceholder =
      _.isEmpty(value) && isAITutor && isChatEmpty && !isAudioTranscriptLoading;

    const hasAttachments = !_.isEmpty(localAttachments);

    return showPlaceholder && _.isEmpty(selectedLearningTool) ? (
      <span
        className={classNames(classes.placeholder, {
          [classes.placeholderWithAttachments]: hasAttachments,
        })}
      >
        <span>
          {t("ai:start_by_defining_your_tutoring_objective_placeholder")}
          &nbsp;
        </span>
        <span
          className={classes.animatedPlaceholderText}
          onAnimationEnd={handlePlaceholderAnimationEnd}
        >
          {t("ai:exit_ticket_for_a_lesson_on_periodic_table_elements")}
        </span>
      </span>
    ) : null;
  };

  const renderAttachments = attachments => {
    if (_.isEmpty(attachments)) return null;

    return (
      <div
        className={classes.attachmentListContainer}
        ref={attachmentScrollRef}
      >
        {_.map(attachments, (attachment, index) => {
          const { id, name, url, type, mimeType, metadata } = attachment || {};

          const disabled =
            isModalityAttachmentType(attachment, true) &&
            _.isNil(getAttachmentId(attachment));

          const deleteAttachmentBtn = (
            <IconButton
              dsVersion="2.0"
              onClick={e => {
                e.stopPropagation();
                deleteAttachment({ id });
              }}
              variant="neutral"
              type="outlined"
              size="x-small"
              className={
                isTouchableDevice
                  ? classes.deleteIconForTouchable
                  : classes.deleteIcon
              }
              icon={<CloseOutlined size="xxxx-small" />}
              testId={`aiChatFooter-deleteAttachment`}
              disabled={disabled}
            />
          );

          const onClickAttachmentCard = () => {
            // When clicked on modalities attachment do not open theatre mode
            if (contextOnMediaClick && isModalityAttachmentType(attachment)) {
              contextOnMediaClick({ attachment });
              return;
            }

            // skip theatre mode for link
            if (type === "LINK" || type === "VIDEO") {
              openLinkInNewTab({ url });
            } else {
              setMediaViewerModalState({
                attachments,
                activeAttachmentIndex: index,
              });
            }
          };

          const attachmentClass = classNames(classes.cardContainer, {
            [classes.removing]: id === removingAttachmentId,
          });

          switch (type) {
            case "AUDIO": {
              const audioRecorderClass = classNames(
                classes.cardContainer,
                classes.audioRecorderContainer,
                classes.audioRecorder
              );

              const showGenerateTranscript =
                getIsAITutorEnhancementsV2Enabled() && !isSpeakingOnly;

              const buttonElement = showGenerateTranscript ? (
                <ConditionalWrapper
                  condition={!isTouchableDevice}
                  type="wrapperAsElement"
                  wrapper={
                    <Tooltip
                      showArrow
                      placement="top"
                      tooltip="Generate transcript"
                    />
                  }
                >
                  <IconButton
                    variant="neutral"
                    type="plain"
                    size="small"
                    icon={<TextAlignLeftOutlined />}
                    disabled={isAudioTranscriptLoading}
                    testId="aiChat-footer-toggle-editable"
                    onClick={() => handleGenerateTranscript(attachment)}
                  />
                </ConditionalWrapper>
              ) : null;

              return (
                <div
                  className={audioRecorderClass}
                  key={id}
                  ref={attachmentsContainerRef}
                >
                  <CompactAudioWaveformPlayer
                    attachment={attachment}
                    containerStyle={styles.waveformContainerStyle}
                    testId="aiChat-footer"
                    buttonElement={buttonElement}
                    getPopupContainer={() => document.body}
                  />
                  {!isAudioTranscriptLoading ? deleteAttachmentBtn : null}
                </div>
              );
            }

            default: {
              const { version } = metadata || {};
              const isActivated = selectedModalityId === id;

              const customType = getCustomType(attachment);
              let displayType = customType;

              if (type === "WORKBOOK") {
                displayType = "Whiteboard";
              }
              const subtext = version
                ? `${displayType} • ${t("common:version")} ${version}`
                : displayType;

              return (
                <div className={attachmentClass} key={id}>
                  <MediaCard
                    dsVersion="2.0"
                    name={name}
                    url={url}
                    thumbUrl={url}
                    type={customType}
                    mimeType={mimeType}
                    className={classes.mediaCard}
                    onClick={onClickAttachmentCard}
                    isActivated={isActivated}
                    subtext={subtext}
                  />
                  {deleteAttachmentBtn}
                </div>
              );
            }
          }
        })}
      </div>
    );
  };

  // render attachments
  const renderAttachmentItems = () => {
    // Extract attachment IDs that are already in the value/contenteditable
    const attachmentIdsInValue = extractAttachmentIdsFromValue(value);

    // Filter out attachments that are already displayed in the value
    const filteredAttachments = _.filter(
      localAttachments,
      ({ id }) => !_.includes(attachmentIdsInValue, id)
    );

    const [
      audioAttachments,
      otherAttachments,
    ] = _.partition(filteredAttachments, { type: "AUDIO" });

    return (
      <>
        {renderAttachments(otherAttachments)}
        {renderAttachments(audioAttachments)}
      </>
    );
  };

  const renderContentEditable = () => {
    const uiContentEditableStyles = {
      ...styles.uiContentEditable,
      paddingBottom: isAudioTranscriptLoading ? "8px" : "12px",
      ...(isContentEditableDisabled && { minHeight: "44px" }),
    };

    return shouldRenderContentEditable ? (
      <UIBaseContentEditable
        key={placeholderText}
        value={value}
        minRows={1}
        maxRows={5}
        onKeyDown={onKeyDown}
        onPaste={handlePaste}
        id={CONTENT_EDITABLE_ID}
        onKeyUp={onKeyUpContentEditable}
        placeholder={placeholderText}
        shouldParseInPlainText={false}
        style={uiContentEditableStyles}
        updateInputField={updateInputField}
        onBlurInputField={onBlurInputField}
        disabled={isContentEditableDisabled}
        updateInputRef={setContentEditableRef}
        onDoubleClick={onDoubleClickContentEditable}
        onClickContentEditable={onClickContentEditable}
        onFocusContentEditable={onFocusContentEditable}
      />
    ) : null;
  };

  const renderContextSelection = () => {
    return (
      <div className={classes.container} ref={contextSelectionContainerRef}>
        {_.map(contextOptions, contextOption => {
          const {
            field,
            type,
            label,
            value,
            tooltip,
            isRequired,
            isDisabled,
            disabledTooltip,
          } = contextOption || {};

          const key = `${field}-${type}`;

          const parsedValue = _.trim(
            extractContentFromHTML({ htmlString: value })
          );

          const sanitizedValue = _.isString(value)
            ? getStyleStrippedText({
                text: value,
                type: "SANITIZE_DANGER_HTML_PARAGRAPH",
              })
            : "";

          const __html = sanitizedValue ? sanitizedValue : t("common:select");

          const parsedTooltip = _.trim(
            extractContentFromHTML({ htmlString: tooltip })
          );

          const parsedLabel = cleanLabel(label);

          const onValueClick = () => {
            if (!isDisabled) onClickContextOption(contextOption);
          };

          const handleKeyDown = e => {
            if (e.key === "Enter" || e.key === " ") {
              onValueClick();
            }
          };

          // Determine which tooltip to show for the value
          let valueTooltip;
          if (isDisabled && disabledTooltip) {
            valueTooltip = disabledTooltip;
          } else if (!_.isEmpty(parsedTooltip)) {
            valueTooltip = parsedTooltip;
          } else {
            valueTooltip = parsedValue;
          }

          const showTooltip = isDisabled
            ? !_.isEmpty(disabledTooltip)
            : !_.isEmpty(parsedTooltip) || !_.isEmpty(parsedValue);

          // classes
          const valueClass = classNames("text-body-2", classes.value, {
            [classes.emptyValue]: _.isEmpty(value),
          });

          const valueContainerClass = classNames(classes.valueContainer, {
            [classes.disabledValueContainer]: isDisabled,
            [classes.emptyValueContainer]: _.isEmpty(value),
          });

          const labelClass = classNames("text-body-2", classes.label);

          return (
            <div key={key} className={classes.contextContainer}>
              {/* Label */}
              <ConditionalWrapper
                condition={!isTouchableDevice}
                type="wrapperAsElement"
                wrapper={
                  <Tooltip
                    placement="top"
                    showArrow={true}
                    tooltip={parsedLabel}
                    visible={_.size(parsedLabel) > 25}
                    overlayInnerStyle={styles.overlayInnerStyle}
                  />
                }
              >
                <div className={classes.flex}>
                  <div className={labelClass}>{parsedLabel}</div>
                  {isRequired ? (
                    <span className={classes.asterisk}>{"*"}</span>
                  ) : null}
                </div>
              </ConditionalWrapper>

              {/* Value */}
              <div
                tabIndex={0}
                role="button"
                onClick={onValueClick}
                onKeyDown={handleKeyDown}
                className={valueContainerClass}
              >
                <ConditionalWrapper
                  condition={!isTouchableDevice}
                  type="wrapperAsElement"
                  wrapper={
                    <Tooltip
                      showArrow={true}
                      placement="topLeft"
                      tooltip={valueTooltip}
                      overlayInnerStyle={styles.overlayInnerStyle}
                      visible={_.size(valueTooltip) > 25 && showTooltip}
                    />
                  }
                >
                  <div
                    className={valueClass}
                    dangerouslySetInnerHTML={{ __html }}
                  />
                </ConditionalWrapper>
                {!isDisabled ? (
                  <PencilLineOutlined
                    size="xxx-small"
                    variant="subtle"
                    className={classes.pencilIcon}
                  />
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderContextListWithAnimation = () => {
    return (
      <AnimateHeightWrapper
        height={showContextList && !_.isEmpty(contextOptions) ? "auto" : 0}
        duration={300}
        showContextList={showContextList && !_.isEmpty(contextOptions)}
        style={{ zIndex: 10 }}
      >
        <div className={classes.contextSelectionContainer}>
          {renderContextSelection()}
        </div>
      </AnimateHeightWrapper>
    );
  };

  const renderMediaViewer = () => {
    if (_.isEmpty(mediaViewerData)) {
      return null;
    }

    const { attachments, activeAttachmentIndex } = mediaViewerData;

    const closePreviewModal = () => {
      setMediaViewerModalState(null);
    };

    return (
      <TheatreModeV2
        mediaAttachments={{ attachments }}
        activeAttachmentIndex={activeAttachmentIndex}
        toggleTheatreMode={closePreviewModal}
      />
    );
  };

  const renderFooterActions = () => {
    const _footerOptions = footerOptions || DEFAULT_FOOTER_OPTIONS;

    return (
      <div className={classes.footerOptions}>
        {_.map(_footerOptions, option => {
          const {
            id,
            key,
            label,
            icon,
            isVisible = true,
            tooltip,
            disabledTooltip,
            showTooltip = false,
            disabled = false,
            componentProps,
          } = option;

          if (!isVisible) return null;

          let comp = null;

          const _tooltipLabel = tooltip ? tooltip : label;
          const _isDisabled = disabled;

          const tooltipLabel =
            disabled && disabledTooltip ? disabledTooltip : _tooltipLabel;

          const _onClickFooterOption = () => {
            onClickFooterOption(id);

            // load the saved latex (if any) in math equation editor
            if (key === OPTIONS.ADD_MATH_EQUATION && mathEquationLatex) {
              setSelectedAction({
                action: key,
                params: { latex: mathEquationLatex },
              });
            } else if (isModalityAttachmentType({ type: key }, true)) {
              onCreateModalityAttachment({ key: _.toLower(key) });
            } else {
              setSelectedAction({ action: key });
            }
          };

          switch (id) {
            case "language": {
              comp = (
                <AILanguageSelection
                  conversationId={parentId}
                  entityType={entityType}
                  isDisabled={_isDisabled}
                  conversationMetadata={conversationMetadata}
                  testId={"chatFooter-languageSelection"}
                  buttonSize={"medium"}
                  showIconButton={true}
                  {...componentProps}
                />
              );
              break;
            }

            case "add_attachment": {
              const buttonComponent = (
                <span>
                  <ConditionalWrapper
                    condition={!isTouchableDevice}
                    type="wrapperAsElement"
                    wrapper={
                      <Tooltip
                        tooltip={
                          <span className={classes.tooltipContainer}>
                            {t(tooltipLabel)}
                          </span>
                        }
                        visible={showTooltip && !isCreateDropdownVisible}
                        showArrow={true}
                        placement={"top"}
                      />
                    }
                  >
                    <IconButton
                      id={id}
                      icon={icon}
                      size="medium"
                      dsVersion="2.0"
                      variant="neutral"
                      type="plain"
                      disabled={_isDisabled}
                      isActivated={isCreateDropdownVisible}
                      testId="chatFooter-addAttachment"
                    />
                  </ConditionalWrapper>
                </span>
              );

              comp = (
                <CreateDropDown
                  mode="edit"
                  dsVersion="2.0"
                  multiselect={true}
                  exportAsPdf={true}
                  parentId={parentId}
                  isNewDropdown={true}
                  disabled={_isDisabled}
                  parentType={parentType}
                  accept={ALLOWED_FILE_TYPES}
                  visible={isCreateDropdownVisible}
                  buttonComponent={buttonComponent}
                  dropDownOptions={
                    customCreateDropdownOptions || CREATE_DROPDOWN_OPTIONS
                  }
                  onMultipleFilesUploaded={addAttachment}
                  enableShareModal={false} // do not show DriveShareModal, directly add as a file
                  onVisibleChange={setIsCreateDropdownVisible}
                  customFilterFilesHandler={customFilterFilesHandler}
                  showDisplayText={false} // do not show display text field for link (not required for AI)
                  onDropDownOptionClick={onDropDownOptionClick}
                />
              );
              break;
            }

            case "learning_tools": {
              comp = (
                <LearningToolsDropdown
                  onToolSelected={handleLearningToolSelection}
                  disabled={_isDisabled}
                  tooltipLabel={t(tooltipLabel)}
                  testId={`chatFooter-learningTools`}
                />
              );
              break;
            }

            default: {
              comp = (
                <ConditionalWrapper
                  condition={!isTouchableDevice}
                  type="wrapperAsElement"
                  wrapper={
                    <Tooltip
                      tooltip={
                        <span className={classes.tooltipContainer}>
                          {t(tooltipLabel)}
                        </span>
                      }
                      visible={showTooltip}
                      showArrow={true}
                    />
                  }
                >
                  <IconButton
                    id={id}
                    icon={icon}
                    dsVersion="2.0"
                    type="plain"
                    variant="neutral"
                    onClick={_onClickFooterOption}
                    disabled={disabled}
                    testId={`chatFooter-${_.replace(id, "_", "-")}`}
                  />
                </ConditionalWrapper>
              );
            }
          }

          return <React.Fragment key={id}>{comp}</React.Fragment>;
        })}
      </div>
    );
  };

  // render modals for math equation, code, graph etc.
  const renderModals = () => {
    const closeModal = () => setSelectedItem({ action: null });

    switch (selectedItem?.action) {
      case OPTIONS.ADD_IMAGE_FROM_CAMERA: {
        const updatePost = async ({ attachments }) => {
          if (!_.isEmpty(attachments)) {
            setSelectedAction({ action: null });

            const uploadedAttachments = await uploadAttachments({
              attachments,
              parentType,
              parentId,
            });

            const attachmentArray = _.map(uploadedAttachments, attachment => {
              return _.omit(attachment, ATTACHMENT_OMIT_ATTRIBUTES);
            });

            addAttachment(attachmentArray);
          }
        };

        const screenPanelProps = {
          onCloseClick: closeModal,
          updatePost,
          postDetails: { attachments: [] },
        };

        return (
          <UIModal isOpen isFullScreen>
            <CaptureImage
              setToastMsg={setToastMsg}
              screenPanelProps={screenPanelProps}
              showDesignV2={true}
              hideGlobalTray={true}
              title={t("journal:take_photo")}
            />
          </UIModal>
        );
      }

      case OPTIONS.ADD_MATH_EQUATION: {
        const closeEditor = ({ latex = "" } = {}) => {
          // save only when user opened the editor to add new equation
          // if user is editing existing equation then do not save the latex

          if (_.isEmpty(selectedItem?.params?.equationId)) {
            updateAIData({ mathEquationLatex: latex });
          }

          closeModal();
        };

        const handleInsertEquation = params => {
          const { height, width, latex, verticalAlign, src } = params;

          // clear the math equation latex from redux once equation is inserted
          updateAIData({ mathEquationLatex: "" });

          const targetDataId = _.get(selectedItem, "params.equationId");

          // if user is creating new equation (targetDataId is undefined)
          // if caret position (savedSelectionRef) is saved then insert at that position

          if (!targetDataId && savedSelectionRef.current && !_.isEmpty(value)) {
            restoreCaretPositionAndInsertImage(params);
          } else {
            // if user is editing existing equation and update that equation
            const randomId = generateRandomId();

            const imgElement = (
              <img
                src={src}
                className="math-equation"
                height={height}
                width={width}
                style={{
                  verticalAlign: `${verticalAlign}px`,
                  display: "inline",
                }}
                alt={latex}
                data-latex={latex}
                data-id={randomId}
              />
            );

            const newEquation = ReactDOMServer.renderToStaticMarkup(imgElement);

            const updatedValue = replaceMathEquation({
              value,
              newEquation,
              targetDataId,
            });

            updateInputField(updatedValue);
          }
        };

        return (
          <DSProvider dsVersion="1.0">
            <MathEquationEditor
              closeEditor={closeEditor}
              onInsertButtonClick={handleInsertEquation}
              latex={selectedItem?.params?.latex}
            />
          </DSProvider>
        );
      }

      default:
        return null;
    }
  };

  const renderConfigureAIVoice = () => {
    const { gradeId } = props;

    const enableAIPromptSetSelection = getSettingValue({
      name: "Common:EnableAIPromptSetSelection",
      readFromUserAndOrgLevel: true,
    });

    if (!enableAIPromptSetSelection) return null;

    return customConfigureAIVoiceComp ? (
      customConfigureAIVoiceComp()
    ) : (
      <ConfigureAIVoice
        customRef={null}
        gradeId={gradeId}
        entityType={entityType}
        conversationId={parentId}
        showLanguageDropdown={false}
        iconButtonProps={iconButtonProps}
        promptSetEntityType={entitySubType}
        headerSubtitle={t("classRoom:ai_voice_modal_subtext")}
      />
    );
  };

  const renderAudioRecorder = () => {
    if (isWritingOnly) return null;

    const onAudioAttachmentUploadFinished = async attachment => {
      const existingAttachment = _.find(localAttachments, att => {
        return _.startsWith(att.url, "blob:") && att.type === "AUDIO";
      });
      if (!_.isNil(existingAttachment)) {
        // update existing attachment
        setLocalAttachments(prevItems => {
          const updatedAttachments = _.map(prevItems, att => {
            if (att.id === existingAttachment?.id)
              return { ...attachment, id: att.id, title: att.name || null };
            else return att;
          });

          return updatedAttachments;
        });

        if (isStudentAITutor && isSpeakingOnly) {
          onSendMessageLocal();
          setIsAIGeneratingResponse(true);
        }
      }
    };

    const hasAudioAttachment = _.some(localAttachments, { type: "AUDIO" });

    const isAudioRecorderDisabled =
      isAudioTranscriptLoading || hasAudioWithBlobURL;

    const recordButtonType =
      isStudentAITutor && isSpeakingOnly
        ? hasAudioAttachment
          ? "outlined"
          : "fill"
        : "plain";

    const buttonComponent = (
      <ConditionalWrapper
        condition={!isTouchableDevice}
        type="wrapperAsElement"
        wrapper={
          <Tooltip
            tooltip={
              <span className={classes.tooltipContainer}>
                {t("common:record_message")}
              </span>
            }
            visible={true}
            showArrow={true}
          />
        }
      >
        <IconButton
          type={recordButtonType}
          size="medium"
          dsVersion="2.0"
          variant={
            isStudentAITutor && isSpeakingOnly && !hasAudioAttachment
              ? "progressive"
              : "neutral"
          }
          ref={recordButtonRef}
          icon={<MicrophoneOutlined />}
          onClick={deleteExistingAudioAttachment}
          testId="chatFooter-audioButton"
          disabled={isAudioRecorderDisabled}
        />
      </ConditionalWrapper>
    );

    const lastAssistantMessage = _.findLast(
      messages,
      msg => msg?.node?.authorType === "assistant"
    );

    const id = lastAssistantMessage?.node?.id;
    const entry = ttsAudioRegistry?.get(id);

    const audioElement = entry?.audioEl;
    const isAudioReady = entry?.isReady;
    const hasAudioError = entry?.hasError || audioElement?.error;
    const hasAudioEnded = entry?.hasEnded;

    const isStreaming =
      audioElement && isAudioReady && !hasAudioError && !hasAudioEnded;

    const speakingOnlyButtonComponent = (
      <div className={classes.startSpeakingButtonWrapper}>
        {isAIGeneratingResponse || isWritingMessage || isStreaming ? (
          <Button
            type={"outlined"}
            size="medium"
            dsVersion="2.0"
            variant={"neutral"}
            onClick={e => {
              e.stopPropagation();
              onClickStopLocal();
            }}
            isFullWidth={true}
            testId="chatFooter-speakingOnly-stopButton"
          >
            {t("ai:stop_generating")}
          </Button>
        ) : (
          <Button
            type={"fill"}
            size="medium"
            dsVersion="2.0"
            variant={"progressive"}
            ref={recordButtonRef}
            icon={<VoiceWaveOutlined />}
            onClick={deleteExistingAudioAttachment}
            isFullWidth={true}
            testId="chatFooter-speakingOnly-audioButton"
          >
            {t("ai:start_talking")}
          </Button>
        )}
      </div>
    );

    const speechToTextParams = { entityId, entityType: entitySubType };

    const isStudentSpeakingOnly = isStudentAITutor && isSpeakingOnly;

    return (
      <div className={classes.flex}>
        <AudioRecorderSelector
          showValue={false}
          parentId={parentId}
          showProgress={false}
          parentType={parentType}
          onFileUpload={onFileUpload}
          container={audioRecorderNodeRef}
          buttonComponent={
            isStudentSpeakingOnly
              ? speakingOnlyButtonComponent
              : buttonComponent
          }
          recordButtonRef={recordButtonRef}
          version={audioVersion || "inline"}
          setChatbarVisible={handleRecording}
          header={t("common:add_voice_message")}
          updateInputField={
            !showAudioTranscript && onAudioAttachmentUploadFinished
          }
          loaderStyles={styles.aiTutorRecorderStyle}
          onBlobAttachmentRecieved={onBlobAttachmentRecieved}
          disabled={isAttachmentUploading || isAudioTranscriptLoading}
          audioRecorderVisualizerStyle={styles.audioRecorderVisualizer}
          isTouchableDevice={isTouchableDevice}
          speechToTextParams={speechToTextParams}
          source={"AI_TUTOR"}
          buttonWrapperStyle={styles.fullWidth}
          shouldAwaitUploadFile={isStudentSpeakingOnly}
          saveDraftRecording={false}
          minRecordingDurationMs={1000}
        />
      </div>
    );
  };

  const sendOrStopButton = () => {
    if (isAIGeneratingResponse || isWritingMessage) {
      // stop button
      return (
        <IconButton
          dsVersion="2.0"
          size="medium"
          variant="neutral"
          type="plain"
          icon={<SquareFilled />}
          onClick={onClickStopLocal}
          testId="chatFooter-stopButton"
        />
      );
    }

    if (!_.isEmpty(editMessageId)) {
      return (
        <div className={classes.footerButtonContainer}>
          <Button
            dsVersion="2.0"
            variant="neutral"
            type="outlined"
            onClick={resetChatBarStates}
            testId="chatFooter-cancelEditMessageButton"
          >
            {t("common:cancel")}
          </Button>
          <Button
            dsVersion="2.0"
            variant="progressive"
            onClick={onClickEditMessage}
            testId="chatFooter-editMessageButton"
          >
            {t("common:edit")}
          </Button>
        </div>
      );
    }

    const shouldShowLoader = hasAudioWithBlobURL || isWaitingForUpdates;

    // send button
    return shouldShowLoader ? (
      <CircleLoading
        {...(isSpeakingOnly ? styles.speakingOnlyLoaderProps : {})}
      />
    ) : (
      <IconButton
        dsVersion="2.0"
        size="medium"
        variant="progressive"
        type="fill"
        onClick={() => onSendMessageLocal()}
        disabled={
          !isEveryRequiredFieldFilled ||
          isSendButtonDisabled ||
          isAIGeneratingResponse ||
          isAudioTranscriptLoading
        }
        testId="chatFooter-sendButton"
        icon={<SendFilled variant={"on"} />}
      />
    );
  };

  const renderSendOrStopButton = () => {
    if (isStudentAITutor && isSpeakingOnly && _.isEmpty(localAttachments))
      return null;

    const tooltipLabel = !isEveryRequiredFieldFilled ? (
      <Trans i18nKey="ai:please_fill_the_details_from_context_section">
        <span>
          Please fill in the following details from the ‘Context’ section:
        </span>
        <ul className={classes.list}>
          {_.map(prerequisitesWithEmptyValue, prerequisite => {
            const { field, type, label } = prerequisite || {};
            const key = `${field}_${type}`;
            return <li key={key}>{label}</li>;
          })}
        </ul>
      </Trans>
    ) : null;

    return (
      <div
        className={classes.flex}
        onClick={openContextWindow}
        role="button"
        tabIndex={0}
        onKeyDown={event => {
          if (event.key === "Enter" || event.key === " ") {
            openContextWindow();
          }
        }}
        aria-label={"send or stop button container"}
      >
        <ConditionalWrapper
          condition={!isTouchableDevice}
          type="wrapperAsElement"
          wrapper={
            <Tooltip
              tooltip={
                isAttachmentUploading
                  ? t("classRoom:almost_ready_loading_in_progress")
                  : tooltipLabel
              }
              visible={!isEveryRequiredFieldFilled || isAttachmentUploading}
              placement={"topRight"}
              align={
                !isEveryRequiredFieldFilled
                  ? tooltipAlign.fields
                  : tooltipAlign.attachment
              }
              overlayInnerStyle={styles.overlayInnerStyle}
              showArrow={true}
            />
          }
        >
          {sendOrStopButton()}
        </ConditionalWrapper>
      </div>
    );
  };

  const renderErrorDialogBox = () => {
    const onClickDialogButton = async () => {
      const { action, params } = currentDialogInfo || {};
      const { attachmentId } = params || {};

      switch (action) {
        case MODAL_ERRORS.FILE_COUNT:
        case MODAL_ERRORS.FILE_SIZE: {
          setCurrentDialogInfo(INITIAL_DIALOG_STATE);
          setIsCreateDropdownVisible(false);
          break;
        }

        case MODAL_ERRORS.ZERO_TOKENS: {
          if (attachmentId) {
            await deleteAIConversationAttachment({
              input: { attachmentIds: [attachmentId] },
              aiConversationId: parentId,
            });
          }

          setCurrentDialogInfo(INITIAL_DIALOG_STATE);
          setIsCreateDropdownVisible(false);
          break;
        }
      }
    };

    return !_.isEmpty(currentDialogAction) ? (
      <Modal
        width="440px"
        height="fit-content"
        shouldCloseOnEsc={true}
        isOpen={true}
        shouldCloseOnOverlayClick={false}
        onClose={onClickDialogButton}
      >
        <Modal.Header isClosable={true} hasSeparator={false}>
          <header className={classes.modalTitleContainer}>
            <WarningTriangleFilled variant="critical" />
            <span className={classes.modalTitle}>
              {DIALOGS_INFO[currentDialogAction].title({
                t,
                ...currentDialogParams,
              })}
            </span>
          </header>
        </Modal.Header>
        <Modal.Body isScrollable>
          <span className={classes.modalBody}>
            {DIALOGS_INFO[currentDialogAction].message({
              t,
              ...currentDialogParams,
            })}
          </span>
        </Modal.Body>
        <Modal.Footer hasSeparator={false}>
          <footer className={classes.modalFooter}>
            <Button
              dsVersion="2.0"
              variant="neutral"
              type="outlined"
              size="medium"
              onClick={onClickDialogButton}
              testId="chatFooter-modalButtonTwo"
            >
              {DIALOGS_INFO[currentDialogAction].buttonTwoText({
                t,
                ...currentDialogParams,
              })}
            </Button>
          </footer>
        </Modal.Footer>
      </Modal>
    ) : null;
  };

  const renderLinkifyModal = () => {
    return linkifyModalKey ? (
      <LinkifyModal
        {...attachment}
        mode={selectedAttachmentId ? "edit" : "create"}
        title={_.get(attachment, "name")}
        onClose={onCloseLinkifyModal}
        onClickSave={onInsertAttachment}
        customFilterFilesHandler={customFilterFilesHandler}
        testId="chatFooter-linkifyModal"
        isUrlOnly={linkifyModalKey === "add_a_link"}
        isVideoExplorerSelected={selectedLearningTool?.key === "video_explorer"}
        parentId={parentId}
        parentType={parentType}
        isAttachmentAddedFromPrompt={linkifyModalKey !== "add_a_link"}
      />
    ) : null;
  };

  const renderLearningToolTag = () => {
    if (_.isEmpty(selectedLearningTool)) return null;

    const { label, key } = selectedLearningTool || {};

    const { coloredIcon } = getLearningToolIcon({ key });

    const handleRemoveTag = () => {
      setSelectedLearningTool(null);
      if (!_.isEmpty(selectedPrompt) && key !== "video_explorer") {
        setValue("");
        onChangeSelectedPrompt({});
      } else {
        if (key === "video_explorer") {
          setValue(removeHtmlWithContent(value));
          onChangeSelectedPrompt({});
        }
      }
    };

    return (
      <div className={classes.learningToolTagContainer}>
        {cloneElement(coloredIcon, { size: "xx-small" })}
        <span className="text-body-s">{t(label)}</span>
        <span
          className={classes.closeBtn}
          onClick={handleRemoveTag}
          onKeyDown={e => onEnterOrSpaceKeyDown(e, handleRemoveTag)}
          role="button"
          tabIndex={0}
          aria-label="close button"
        >
          <CloseOutlined size="xxxx-small" variant="subtle" />
        </span>
      </div>
    );
  };

  // ===== classes =====

  const footerOverlayClass = classNames({
    [classes.footerOverlay]: isContextEnabled,
    [classes.footerOverlayWithContextExpanded]: showContextList,
  });

  const contentEditableClass = classNames({
    [classes.contentEditable]: true,
    [classes.aiTutorContentEditable]: isAITutor,
    [classes.contentEditableWithPrompt]:
      !_.isEmpty(selectedPrompt) && isOnlySpanTag(value) && !isAITutor,
  });

  const footerInnerContainerClass = classNames({
    [classes.footerInnerContainer]: true,
    [classes.footerInnerContainerFocused]:
      isContentEditableFocused && entitySubType === "ai_tutor",
    [classes.hidden]: isRecording && audioVersion === "visualiser",
    [classes.hideBorder]: isSpeakingOnly && isStudentAITutor,
  });

  const actionsContainerClass = classNames({
    [classes.actionsBar]: true,
    [classes.actionBarForSpeakingOnly]: !isWritingOnly && !isContextEnabled,
  });

  return (
    <footer className={classes.footerWrapper} ref={setAudioRecorderNodeRef}>
      {isRecording && audioVersion !== "visualiser" ? (
        <div className={classes.recordingOverlay} />
      ) : null}

      {/* Knowledge bar - Context switching and selection */}
      {isContextEnabled ? renderKnowledgeBar() : null}

      {/* Context list with animation */}
      {renderContextListWithAnimation()}

      <div
        className={footerInnerContainerClass}
        style={footerInnerContainerStyle}
        ref={footerRef}
      >
        {isSpeakingOnly && isStudentAITutor ? (
          renderAudioRecorder()
        ) : (
          <>
            {showQuickTip && (
              <div
                className={classes.quickTip}
                style={elevations["elevation-2B"]}
              >
                <InformationOutlined variant="on" size="xxx-small" />
                <span className={classes.quickTipText}>
                  {t("ai:assessment_quick_tip_msg")}
                </span>
              </div>
            )}

            {/* Overlay for Audio Recorder */}
            {!isWritingOnly && isRecording && audioVersion !== "visualiser" ? (
              <div
                className={classes.recordingOverlay}
                ref={audioContainerRef}
              />
            ) : null}

            {renderLearningToolTag()}
            {renderAttachmentItems()}

            {/* Input box */}
            <div className={contentEditableClass}>
              <DropzoneComponent
                multiple
                shouldUpload
                disableClick
                onContentUploaded={addAttachment}
                renderComponent={renderContentEditable()}
              />
            </div>

            {/* Audio transcribing animation */}
            {isAudioTranscriptLoading ? (
              <div
                className={classNames(classes.voiceLoaderContainer, {
                  [classes.voiceLoaderWithContentEditable]: shouldRenderContentEditable,
                })}
              >
                <div className={classes.voiceLoader}>
                  <div className={classes.voiceBar}></div>
                  <div className={classes.voiceBar}></div>
                  <div className={classes.voiceBar}></div>
                  <div className={classes.voiceBar}></div>
                </div>
                <div className={classNames("text-body", classes.animatingText)}>
                  {t("ai:transcribing")}
                </div>
              </div>
            ) : null}

            {renderPlaceholderWithAnimation()}

            {/* Footer actions */}
            <div className={actionsContainerClass}>
              {renderFooterActions()}

              <div className={classes.flexWithGap}>
                {/* To change prompt set (for internal users only) */}
                {renderConfigureAIVoice()}

                {/* Audio recorder */}
                {renderAudioRecorder()}

                {/* Send/ stop button */}
                {renderSendOrStopButton()}
              </div>
            </div>
          </>
        )}
      </div>

      {/* White linear gradient */}
      {audioVersion !== "visualiser" ? (
        <div className={footerOverlayClass} />
      ) : null}

      {/* errors dialog box */}
      {renderErrorDialogBox()}

      {renderLinkifyModal()}

      {renderMediaViewer()}

      {/* Full Screen Loader */}
      {showFullScreenLoader ? <FullScreenLoader /> : null}

      {/* Render math equation editor, camera etc. */}
      {renderModals()}
    </footer>
  );
};

const mapStateToProps = state => {
  const progressOfUploads = _.get(state, "app_services.progressOfUploads");
  const mathEquationLatex = _.get(state, "classRoom.aiData.mathEquationLatex");

  const isAttachmentUploading = _.some(_.keys(progressOfUploads), key => {
    const { parentType, status } = progressOfUploads[key] || {};
    return parentType === "AI_CONVERSATION" || status === "COPYINGIMAGE";
  });

  const showAIQuickTip = _.get(state, "assessment.showAIQuickTip", false);

  return {
    isAttachmentUploading,
    mathEquationLatex,
    showAIQuickTip,
  };
};

const mapActionCreators = {
  updateAIData,
  setToastMsg,
  hideIntercom,
  updateAttachment,
  uploadAttachments,
  openLinkInNewTab,
  handleFileUploadAndAdd,
  getSpeechToTextTranscript,
  deleteAIConversationAttachment,
  createAIModalityAttachment,
  getAttachmentFromCache,
  updateAIMessage,
  deleteAiMessages,
  createAIMessage,
  updateShowAIQuickTip,
};

Footer.propTypes = {
  parentType: PropTypes.string,
  onClickStop: PropTypes.func,
  onSendMessage: PropTypes.func,
  showAudioTranscript: PropTypes.bool,
  onAttachmentsUpdate: PropTypes.func,
  onClickFooterOption: PropTypes.func,
  onClickContextOption: PropTypes.func,
  onChangeSelectedPrompt: PropTypes.func,
  footerInnerContainerStyle: PropTypes.object,
};

Footer.defaultProps = {
  onClickStop: _.noop,
  onSendMessage: _.noop,
  testId: "aichat-footer",
  showAudioTranscript: false,
  onAttachmentsUpdate: _.noop,
  onClickFooterOption: _.noop,
  onClickContextOption: _.noop,
  onChangeSelectedPrompt: _.noop,
  parentType: "AI_CONVERSATION",
  footerInnerContainerStyle: {},
};

export default connect(mapStateToProps, mapActionCreators)(Footer);
